import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Tabs,
  Tab,
  Paper,
  Tooltip,
  IconButton,
  Snackbar,
  Alert
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { AdPlacementConfig } from '../services/publisherConfigService';
import { getPythonTemplate } from './codeSnippets/pythonTemplate';
import { getJavaScriptTemplate } from './codeSnippets/javascriptTemplate';
import SyntaxHighlighter from 'react-syntax-highlighter';
import { atomOneDark } from 'react-syntax-highlighter/dist/esm/styles/hljs';

interface CodeGeneratorProps {
  config: AdPlacementConfig;
  publisherId: string;
  publisherName: string;
}

const AdPlacementCodeGenerator: React.FC<CodeGeneratorProps> = ({
  config,
  publisherId,
  publisherName
}) => {
  const [language, setLanguage] = useState<string>('python');
  const [copied, setCopied] = useState(false);
  const [downloaded, setDownloaded] = useState(false);

  const getCodeSnippet = () => {
    switch (language) {
      case 'javascript':
        return getJavaScriptTemplate(publisherId, publisherName, config);
      case 'python':
      default:
        return getPythonTemplate(publisherId, publisherName, config);
    }
  };

  const codeSnippet = getCodeSnippet();

  const handleLanguageChange = (event: React.SyntheticEvent, newValue: string) => {
    setLanguage(newValue);
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(codeSnippet);
    setCopied(true);
  };

  const downloadCode = () => {
    const extension = language === 'python' ? 'py' : 'js';
    const filename = `reasonic_integration.${extension}`;
    const blob = new Blob([codeSnippet], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    anchor.click();
    
    URL.revokeObjectURL(url);
    setDownloaded(true);
  };

  const handleSnackbarClose = () => {
    setCopied(false);
    setDownloaded(false);
  };

  return (
    <Card variant="outlined" sx={{ mb: 4 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Integration Code
        </Typography>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          This code will help you integrate Reasonic's ad placement in your application.
        </Typography>
        
        <Tabs
          value={language}
          onChange={handleLanguageChange}
          sx={{ mb: 2 }}
        >
          <Tab label="Python" value="python" />
          <Tab label="JavaScript" value="javascript" />
        </Tabs>
        
        <Box sx={{ position: 'relative' }}>
          <Paper 
            elevation={1}
            sx={{ 
              p: 0,
              position: 'relative',
              borderRadius: 1,
              overflow: 'hidden',
              maxHeight: '400px'
            }}
          >
            <Box 
              sx={{ 
                position: 'absolute',
                top: 8,
                right: 8, 
                zIndex: 2,
                display: 'flex',
                gap: 1
              }}
            >
              <Tooltip title="Copy code">
                <IconButton 
                  size="small" 
                  onClick={copyToClipboard}
                  sx={{ 
                    backgroundColor: 'rgba(0,0,0,0.3)',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: 'rgba(0,0,0,0.5)',
                    }
                  }}
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Download">
                <IconButton 
                  size="small"
                  onClick={downloadCode}
                  sx={{ 
                    backgroundColor: 'rgba(0,0,0,0.3)',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: 'rgba(0,0,0,0.5)',
                    }
                  }}
                >
                  <FileDownloadIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            
            <SyntaxHighlighter
              language={language}
              style={atomOneDark}
              customStyle={{ margin: 0, maxHeight: '400px' }}
              showLineNumbers={true}
            >
              {codeSnippet}
            </SyntaxHighlighter>
          </Paper>
        </Box>
        
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <Button 
            variant="contained" 
            startIcon={<ContentCopyIcon />}
            onClick={copyToClipboard}
          >
            Copy Code
          </Button>
        </Box>
      </CardContent>
      
      <Snackbar open={copied} autoHideDuration={3000} onClose={handleSnackbarClose}>
        <Alert onClose={handleSnackbarClose} severity="success" sx={{ width: '100%' }}>
          Code copied to clipboard!
        </Alert>
      </Snackbar>
      
      <Snackbar 
        open={downloaded} 
        autoHideDuration={3000} 
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleSnackbarClose} severity="success" sx={{ width: '100%' }}>
          Code downloaded successfully!
        </Alert>
      </Snackbar>
    </Card>
  );
};

export default AdPlacementCodeGenerator; 